import createTrackingEventName from "app/pages/sales-intelligence/utils/testing/createTrackingEventName";
import type { track as TrackFunction } from "pro-features/components/WithTrack/src/WithTrack";
import type { CustomAttributes } from "app/services/track/track";

import type { PocessedMessageData } from "../../store/types";

import { SuggestionType } from "./types";
import { getFilterChangesList } from "./utils";

const createLeadGeneratorTrackingService = (track: TrackFunction) => {
    const trackLeadGeneratorEvent = (action: string, eventName: string, customAttributes?: CustomAttributes): void => {
        track("filters panel - agent", action, eventName, customAttributes);
    };

    return {
        trackSendMessage(messageLength?: number) {
            trackLeadGeneratorEvent("click", "send query", { messageLength: messageLength || 0 });
        },

        trackHumanMessageSubmit(queryText: string | undefined) {
            if (!queryText) {
                return;
            }

            const eventName = createTrackingEventName("send query", queryText);

            trackLeadGeneratorEvent("click", eventName);
        },

        trackShowMoreClick() {
            trackLeadGeneratorEvent("click", "show more");
        },

        trackGenerateMoreClick(generationSequenceSize: number) {
            const eventName = createTrackingEventName("generate more", undefined, generationSequenceSize);

            trackLeadGeneratorEvent("click", eventName);
        },

        trackSuggestedQueryClick(queryText: string, generationSequenceSize: number) {
            const eventName = createTrackingEventName(
                "select suggestion",
                generationSequenceSize === 0 ? SuggestionType.InitialSuggestion : SuggestionType.GeneratedSuggestion,
                queryText,
            );

            trackLeadGeneratorEvent("click", eventName);
        },

        trackContextualSuggestionClick(suggestionText: string) {
            const eventName = createTrackingEventName(
                "select suggestion",
                SuggestionType.FollowupSuggestion,
                suggestionText,
            );

            trackLeadGeneratorEvent("click", eventName);
        },

        trackRemovedFilterValue(filterName: string, filterValue: string) {
            const eventName = createTrackingEventName("remove value", `${filterName}: ${filterValue}`);

            trackLeadGeneratorEvent("click", eventName);
        },

        trackSaveSearchClick(filterNameList: readonly string[], queryNumber: number) {
            const eventName = createTrackingEventName("save search", filterNameList, queryNumber);

            trackLeadGeneratorEvent("click", eventName);
        },

        trackUpdateSearchClick(filterNameList: readonly string[], queryNumber: number) {
            const eventName = createTrackingEventName("update search", filterNameList, queryNumber);

            trackLeadGeneratorEvent("click", eventName);
        },

        trackSaveAsNewSearchClick(filterNameList: readonly string[], queryNumber: number) {
            const eventName = createTrackingEventName("save as new", filterNameList, queryNumber);

            trackLeadGeneratorEvent("click", eventName);
        },

        trackAgentMessageFiltersResponse(messageData: PocessedMessageData | null) {
            if (!messageData) {
                return;
            }

            const filterChangesList = getFilterChangesList(messageData);
            if (filterChangesList.length === 0) {
                return;
            }

            filterChangesList.forEach(({ changeType, filterName, filterValue, isSignal }) => {
                const eventName = createTrackingEventName(filterName, filterValue, isSignal ? "signal" : "");

                trackLeadGeneratorEvent(changeType, eventName);
            });
        },
    };
};

export default createLeadGeneratorTrackingService;
